import { Flex, Text } from '@chakra-ui/react'
import { useAtom } from 'jotai'
import { Download } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import MangoLogo from '~/assets/svgs/icon-mango-logo.svg?react'
import { DesktopAppLaunchConfirmModal } from '~/components/DesktopAppLaunchConfirmModal'
import { TextButtonVariants } from '~/components/shared/componentStyles/TextButton.style'
import {
  DropdownSelect,
  SelectItem,
} from '~/components/shared/Dropdown/DropdownSelect'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { TextButton } from '~/components/shared/TextButton'
import {
  usePartnerDesktopAppDownloadUrl,
  usePartnerDesktopAppName,
} from '~/hooks/useCurrentPartner'
import { useDesktopAppLaunchConfirm } from '~/hooks/useDesktopAppLaunchConfirm'
import { toDesktopAppModeState } from '~/pages/Home/Banners/utils/desktopAppDetectionBannerLogic'
import {
  desktopAppDetectionBannerStateAtom,
  DesktopAppMode,
  getDesktopAppMode,
} from '~/pages/Home/Banners/utils/desktopAppDetectionBannerLogicState'
import { useProfileSettingsStyle } from '~/pages/Settings/componentStyles/ProfileSettings.style'
import { buildDeepLink, openDesktopApp } from '~/utils/desktopDeepLink'
import { openInNewTab } from '~/utils/functions'

const settingsDeepLink = buildDeepLink('settings')

const launchDesktopAppSettings = () => openDesktopApp(settingsDeepLink)

/**
 * Per-browser desktop-app preference. Choosing "Always open in desktop app"
 * first test-launches the app and only saves once the user confirms it opened,
 * so a browser without the app installed never starts handing links off.
 */
export const DesktopAppSettings = () => {
  const { t } = useTranslation()
  const styles = useProfileSettingsStyle()
  const [bannerState, setBannerState] = useAtom(
    desktopAppDetectionBannerStateAtom,
  )
  const mode = getDesktopAppMode(bannerState)
  const launchConfirm = useDesktopAppLaunchConfirm()
  const appName = usePartnerDesktopAppName()
  const downloadUrl = usePartnerDesktopAppDownloadUrl()

  const modeItems = useMemo<SelectItem<DesktopAppMode>[]>(() => {
    const labels: Record<DesktopAppMode, string> = {
      [DesktopAppMode.Ask]: t(
        'profileSettings.desktopApp.modes.ask',
        'Ask me (show a banner)',
      ),
      [DesktopAppMode.Always]: t(
        'profileSettings.desktopApp.modes.always',
        'Always open in the desktop app',
      ),
      [DesktopAppMode.Never]: t(
        'profileSettings.desktopApp.modes.never',
        "Don't ask",
      ),
    }
    return Object.values(DesktopAppMode).map((value) => ({
      content: <MenuListItem key={value} primaryText={labels[value]} />,
      selectedLabel: labels[value],
      value,
    }))
  }, [t])

  const handleSelectMode = (nextMode: DesktopAppMode) => {
    if (nextMode === mode) return
    if (nextMode === DesktopAppMode.Always) {
      launchConfirm.launch(settingsDeepLink)
      return
    }
    setBannerState(toDesktopAppModeState(nextMode))
  }

  const handleConfirmOpened = () => {
    setBannerState(toDesktopAppModeState(DesktopAppMode.Always))
    launchConfirm.close()
  }

  return (
    <Flex css={styles.helpSection} data-testid="desktop-app-settings">
      <Flex css={styles.helpTitleWrapper}>
        <Text css={styles.helpTitleText}>
          {t('profileSettings.desktopApp.label', 'Desktop App')}
        </Text>
      </Flex>
      <Flex flexDirection="column" gap="0.25rem" width="100%">
        <Text fontSize=".875rem" fontWeight="500">
          {t('profileSettings.desktopApp.modeLabel', {
            appName,
            defaultValue: 'When I open a {{appName}} link in this browser',
          })}
        </Text>
        <DropdownSelect
          items={modeItems}
          selectedValue={mode}
          onSelect={handleSelectMode}
        />
        <Text fontSize=".75rem" color="fg.muted">
          {t(
            'profileSettings.desktopApp.modeHint',
            'This setting only applies to this browser.',
          )}
        </Text>
      </Flex>
      <TextButton
        variant={TextButtonVariants.Info}
        leftIcon={MangoLogo}
        onLinkClick={launchDesktopAppSettings}
      >
        {t('profileSettings.openDesktopApp', 'Open Desktop App')}
      </TextButton>
      <TextButton
        variant={TextButtonVariants.Info}
        leftIcon={Download}
        onLinkClick={() => openInNewTab(downloadUrl)}
      >
        {t('profileSettings.downloadApp', 'Download App')}
      </TextButton>
      <DesktopAppLaunchConfirmModal
        launchConfirm={launchConfirm}
        onConfirmed={handleConfirmOpened}
      />
    </Flex>
  )
}
